import type { ComponentChildren } from 'preact';
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { services } from '../app';
import {
  surveyShown,
  hideLabels,
  Panel, goLive, panel, place, pointingAt, selection, settings, showToast, simNow, timeLive, timeRate, timeVersion, toast, viewMode,
  endPreview, previewFrom,
  skyBand,
  loadJSON, saveJSON,
  pointingPaused, pointingPrompt,
  reliveFocus,
} from '../state';
import { dayKey, fmtDate, fmtDateTime, fmtTime } from './format';
import { altAzToEnu, enuToAltAz, refractEnu } from '../lib/math';
import { Icon } from './icons';
import { viewUrl } from './share';
import { nameClipKey } from '../live/names';
import { speak, speaking, unlockNarration } from '../audio/narrator';
import { explainStart, resumePointing, startPointing, stopPointing } from './pointing';
import { aligning } from '../live/align';
import { beginAligning } from './Align';
import { N } from '../data/guide';

export function open(p: Panel) {
  panel.value = panel.peek() === p ? null : p;
}

// ---------- Dialogs and modal layers ----------
// One place decides what a dialog does with the focus and with the page behind it. Every dialog takes the focus when
// it opens and gives it back when it closes: to where it was, or to where the dialog it replaced was opened from. A
// modal dialog is a layer over the page: everything else is out of reach (inert) until it closes, including anything
// that appears meanwhile, and Tab and Shift+Tab go round its own controls. What is modal is what says so to screen
// readers (aria-modal): sheets unless small (the object and time cards leave the sky in reach), the welcome and the
// films. Help's "Show me around" and the What's new card keep their own handling (Guide.tsx, WhatsNew.tsx). A modal
// sheet also dims the page behind it, and a tap there closes it (Sheet, below).

/** The open modal layers, newest last: only the newest answers Tab and Escape. */
const layers: HTMLElement[] = [];
/** Elements made inert here, with how many things want them so (layers, and the dock's and top bar's own rules). */
const holds = new Map<Element, number>();
/** Live regions stay in reach of every layer, so what happens meanwhile (a toast) is still read out. */
const LIVE = '[aria-live], [role="status"], [role="alert"]';

export function holdInert(el: Element) {
  const n = holds.get(el) ?? 0;
  holds.set(el, n + 1);
  if (!n) (el as HTMLElement).inert = true;
}
export function releaseInert(el: Element) {
  const n = holds.get(el) ?? 0;
  if (n > 1) return void holds.set(el, n - 1);
  holds.delete(el);
  if (n) (el as HTMLElement).inert = false;
}

/** Whether a dialog is on top: the newest modal layer, or any dialog while no layer is open. */
export const onTop = (el: Element | null) => !layers.length || layers[layers.length - 1] === el;

/**
 * Where Tab (or Shift+Tab, `back`) must send the focus to keep it inside a modal layer, as an index into its `count`
 * controls in Tab order; null when the browser's own next stop is inside anyway. `at` is where the focus is among
 * them: the index of the control it is on, a half-way index between two (2.5) when it is on something else in the
 * layer (-0.5 before the first), or null when it is outside the layer.
 */
export function tabTarget(count: number, at: number | null, back: boolean): number | null {
  if (!count) return null;
  if (at === null) return back ? count - 1 : 0;
  if (back) return at <= 0 ? count - 1 : null;
  return at >= count - 1 ? 0 : null;
}

function keepTabIn(layer: HTMLElement, e: KeyboardEvent) {
  const stops = [...layer.querySelectorAll<HTMLElement>('a[href], button, input, select, textarea, summary, iframe, [tabindex], [contenteditable]')]
    .filter((x) => x.tabIndex >= 0 && !x.matches(':disabled') && (x.checkVisibility ? x.checkVisibility({ visibilityProperty: true }) : x.getClientRects().length > 0));
  const a = document.activeElement;
  let at: number | null = null;
  if (a && layer.contains(a)) {
    const i = stops.indexOf(a as HTMLElement);
    at = i >= 0 ? i : stops.filter((x) => a.compareDocumentPosition(x) & Node.DOCUMENT_POSITION_PRECEDING).length - 0.5;
  }
  const to = tabTarget(stops.length, at, e.shiftKey);
  if (to === null && stops.length) return;
  e.preventDefault();
  (to === null ? layer : stops[to]).focus();
}

/**
 * Make `el` a modal layer over the page, until the function returned is called. A `backdrop` beside it (a sheet's
 * dimmed backdrop) belongs to the layer: it stays in reach, so that a tap on it can close the layer.
 */
export function openLayer(el: HTMLElement, backdrop?: Element | null): () => void {
  layers.push(el);
  const held = new Set<Element>();
  // Out of reach: everything beside the layer and beside each element on its way up to the page, except its
  // backdrop, live regions and any newer layer opened over this one.
  const take = (x: Node, path: Element) => {
    if (x.nodeType !== 1 || x === path || x === backdrop || held.has(x as Element)) return;
    const e = x as Element;
    if (e.matches(LIVE) || layers.slice(layers.indexOf(el) + 1).some((l) => e.contains(l))) return;
    held.add(e);
    holdInert(e);
  };
  const watching: MutationObserver[] = [];
  for (let path: Element = el; path.parentElement && path !== document.body; path = path.parentElement) {
    const at = path, parent = path.parentElement;
    for (const x of [...parent.children]) take(x, at);
    // And whatever appears there while the layer is open (a chip, a banner, a card).
    const mo = new MutationObserver((records) => {
      for (const r of records) {
        r.addedNodes.forEach((x) => take(x, at));
        r.removedNodes.forEach((x) => held.delete(x as Element) && releaseInert(x as Element));
      }
    });
    mo.observe(parent, { childList: true });
    watching.push(mo);
  }
  const key = (e: KeyboardEvent) => {
    if (e.key === 'Tab' && !e.defaultPrevented && layers[layers.length - 1] === el) keepTabIn(el, e);
  };
  addEventListener('keydown', key);
  return () => {
    removeEventListener('keydown', key);
    watching.forEach((mo) => mo.disconnect());
    held.forEach(releaseInert);
    layers.splice(layers.indexOf(el), 1);
  };
}

/** Where the focus goes back to, from a dialog that closed straight into another: handed on to that one. */
let handedOn: HTMLElement | null = null;
/** An element that can have the focus: on the page and not behind a layer. */
const reachable = (x: Element | null): x is HTMLElement => !!x && x !== document.body && x.isConnected && !x.closest('[inert]');

/**
 * A dialog's manners, in one place. The focus goes back, when it closes, to where it was before it opened (unless
 * something else has taken it by then); and while `modal`, the dialog is a modal layer over the page, with its
 * `backdrop`, if it has one.
 */
export function useDialog(ref: { current: HTMLElement | null }, modal: boolean, backdrop?: { current: Element | null }) {
  // Layout effects run before a dialog opening in this one's place takes the focus.
  useLayoutEffect(() => {
    const a = document.activeElement;
    const back = a && a !== document.body && a.isConnected ? (a as HTMLElement) : handedOn;
    return () => {
      handedOn = back;
      // Once the page has settled (the dock and the top bar back in reach), and only if the focus went with the dialog.
      setTimeout(() => {
        if (handedOn === back) handedOn = null;
        if (reachable(document.activeElement)) return;
        // Back where it was; or, if that has gone (the What's new card that opened the calendar, say), to the dock.
        const to = reachable(back) ? back : document.querySelector<HTMLElement>('.dock button, .topbar button');
        if (reachable(to)) to.focus({ preventScroll: true });
      }, 0);
    };
  }, []);
  useLayoutEffect(() => {
    const el = ref.current;
    if (modal && el) return openLayer(el, backdrop?.current);
  }, [modal]);
}

/** Keep an element out of reach while `on`: the dock's and the top bar's own rules, beside the modal layers'. */
function useOutOfReach(ref: { current: Element | null }, on: boolean) {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!on || !el) return;
    holdInert(el);
    return () => releaseInert(el);
  }, [on]);
}

/** A modal sheet gave way straight to another (Settings to Location, say): the page stays dimmed, with no new fade. */
let dimHandedOn = false;

// ---------- Bottom sheet ----------
export function Sheet({ onClose, onMinimise, children, size = 'auto', class: cls = '', label, labelledBy, modal = size !== 'auto' }: {
  onClose: () => void;
  /** Given, the card can be made small: a button beside Close, and pulling it down makes it small instead of closing it. */
  onMinimise?: () => void;
  children: ComponentChildren; size?: 'auto' | 'tall' | 'full'; class?: string; label?: string; labelledBy?: string; modal?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const body = useRef<HTMLDivElement>(null);
  const scrim = useRef<HTMLDivElement>(null);
  const drag = useRef<{ y: number; dy: number } | null>(null);
  const [closing, setClosing] = useState(false);
  const leaving = useRef(closing);
  leaving.current = closing;
  const close = () => {
    setClosing(true);
    setTimeout(onClose, 240);
  };
  const minimise = onMinimise && (() => {
    setClosing(true);
    setTimeout(onMinimise, 240);
  });
  const closeRef = useRef(close);
  closeRef.current = close;
  const pullRef = useRef(close);
  pullRef.current = minimise ?? close;
  useDialog(ref, modal, scrim);
  // A modal sheet dims the page behind it. Straight after another sheet the dim is already there, so it does not
  // fade in again; and the next sheet keeps it, unless it was fading out with this one.
  const rose = useRef(0);
  useLayoutEffect(() => {
    const el = scrim.current;
    if (!el) return;
    rose.current = performance.now();
    if (dimHandedOn) el.style.animation = 'none';
    return () => {
      if (leaving.current) return;
      dimHandedOn = true;
      setTimeout(() => (dimHandedOn = false), 0);
    };
  }, [modal]);
  // A tap on the dim closes the sheet as Close does: only a tap that starts there once the sheet is up, so not the
  // tap that opened it (nor the second of a double tap), and not a drag from the sheet or across the dim.
  const press = useRef<{ id: number; x: number; y: number; moved: boolean } | null>(null);
  // Keyboard: move focus into the card when it opens, and close it with Escape (only the card on top).
  useLayoutEffect(() => {
    ref.current?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && onTop(ref.current)) closeRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  // One-handed: pulling the content down from the top closes the card, as on iOS.
  useEffect(() => {
    const b = body.current, sheet = ref.current;
    if (!b || !sheet) return;
    let x0 = 0, y0 = 0, dy = 0, atTop = false, pulling = false;
    const start = (e: TouchEvent) => {
      x0 = e.touches[0].clientX;
      y0 = e.touches[0].clientY;
      dy = 0;
      pulling = false;
      atTop = b.scrollTop <= 0;
    };
    const move = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!pulling) {
        const d = t.clientY - y0;
        if (!atTop || b.scrollTop > 0 || d < 10 || Math.abs(t.clientX - x0) > d) return;
        pulling = true;
        y0 = t.clientY;
        sheet.classList.add('dragging');
      }
      e.preventDefault();
      dy = Math.max(0, t.clientY - y0);
      sheet.style.transform = `translateY(${dy}px)`;
    };
    const end = () => {
      if (!pulling) return;
      pulling = false;
      sheet.classList.remove('dragging');
      if (dy > 90) pullRef.current();
      else sheet.style.transform = '';
    };
    // A hairline appears under the pinned title once the content scrolls beneath it.
    const scroll = () => sheet.classList.toggle('scrolled', b.scrollTop > 2);
    b.addEventListener('touchstart', start, { passive: true });
    b.addEventListener('touchmove', move, { passive: false });
    b.addEventListener('touchend', end);
    b.addEventListener('touchcancel', end);
    b.addEventListener('scroll', scroll, { passive: true });
    return () => {
      b.removeEventListener('touchstart', start);
      b.removeEventListener('touchmove', move);
      b.removeEventListener('touchend', end);
      b.removeEventListener('touchcancel', end);
      b.removeEventListener('scroll', scroll);
    };
  }, []);
  const onDown = (e: PointerEvent) => {
    drag.current = { y: e.clientY, dy: 0 };
    ref.current?.classList.add('dragging');
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onMove = (e: PointerEvent) => {
    if (!drag.current || !ref.current) return;
    drag.current.dy = Math.max(0, e.clientY - drag.current.y);
    ref.current.style.transform = `translateY(${drag.current.dy}px)`;
  };
  const onUp = () => {
    if (!drag.current || !ref.current) return;
    const dy = drag.current.dy;
    drag.current = null;
    ref.current.classList.remove('dragging');
    // Closing (or making it small) carries on from where the finger let go.
    if (dy > 90) pullRef.current();
    else ref.current.style.transform = '';
  };
  return (
    <>
      {modal && (
        <div
          ref={scrim}
          class={`sheet-scrim ${closing ? 'sheet-scrim-out' : ''}`}
          aria-hidden="true"
          onPointerDown={(e) => (press.current = e.isPrimary && e.button === 0 && performance.now() - rose.current >= 420 ? { id: e.pointerId, x: e.clientX, y: e.clientY, moved: false } : null)}
          // Once it has moved 10 px it is a drag, even if it comes back; a mouse that leaves the dim with its button
          // held is one too (its moves go elsewhere meanwhile).
          onPointerMove={(e) => {
            const p = press.current;
            if (p && p.id === e.pointerId && Math.hypot(e.clientX - p.x, e.clientY - p.y) >= 10) p.moved = true;
          }}
          onPointerLeave={(e) => {
            const p = press.current;
            if (p && p.id === e.pointerId && e.buttons) p.moved = true;
          }}
          onPointerCancel={() => (press.current = null)}
          onClick={(e) => {
            const p = press.current;
            press.current = null;
            if (p && !p.moved && Math.hypot(e.clientX - p.x, e.clientY - p.y) < 10) close();
          }}
        />
      )}
      <div ref={ref} class={`sheet sheet-${size} ${closing ? 'sheet-closing' : ''} ${minimise ? 'can-min' : ''} ${cls}`} role="dialog" aria-modal={modal ? 'true' : undefined} aria-label={labelledBy ? undefined : label} aria-labelledby={labelledBy} tabIndex={-1}>
        <div class="sheet-grip" onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}>
          <span />
        </div>
        {minimise && (
          <button class="sheet-close sheet-min" onClick={minimise} aria-label="Make the card small">
            <Icon name="chevronDown" size={15} stroke={2.6} />
          </button>
        )}
        <button class="sheet-close" onClick={close} aria-label="Close">
          <Icon name="close" size={13} stroke={2.6} />
        </button>
        <div class="sheet-body" ref={body}>{children}</div>
      </div>
    </>
  );
}

export async function shareSky() {
  const s = services.peek();
  if (!s) return;
  let blob: Blob;
  let name = '';
  try {
    // A picture keeps its date: say the actual day, not "Today".
    const when = `${fmtDate(simNow(), true)}, ${fmtTime(simNow())}`;
    blob = await s.engine.snapshot(`${place.peek().name} · ${when}`);
    name = `ahillah-${Date.now()}.jpg`;
  } catch {
    showToast('Could not make the picture');
    return;
  }
  const url = viewUrl(s);
  const text = `The sky over ${place.peek().name}, ${fmtDate(simNow(), true)} at ${fmtTime(simNow())}`;
  const download = () => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(
        () => showToast('Picture saved, and a link to this view copied'),
        () => showToast('Saved a picture of the sky'),
      );
    } else showToast('Saved a picture of the sky');
  };
  const file = new File([blob], name, { type: 'image/jpeg' });
  const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
  if (!nav.share) return download();
  try {
    // The picture and a link that opens this exact view, where the platform allows both.
    if (nav.canShare?.({ files: [file], url, text })) await nav.share({ files: [file], url, text, title: 'The sky from Ahillah' });
    else if (nav.canShare?.({ files: [file] })) await nav.share({ files: [file], text: `${text}: ${url}`, title: 'The sky from Ahillah' });
    else await nav.share({ url, text, title: 'The sky from Ahillah' });
  } catch (e) {
    // The share sheet was refused or failed: save the picture instead.
    if ((e as Error).name !== 'AbortError') download();
  }
}

// ---------- Top bar ----------
export function TopBar() {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, []);
  // Help's "Show me around" lights up the bar's controls without letting them be pressed (behind a modal sheet the
  // whole page is out of reach anyway).
  const bar = useRef<HTMLElement>(null);
  useOutOfReach(bar, panel.value === 'walkthrough');
  void timeVersion.value;
  const live = timeLive.value;
  const rate = timeRate.value;
  const now = simNow();
  const name = place.value.name || `${place.value.lat.toFixed(2)}, ${place.value.lon.toFixed(2)}`;
  // Away from now the pill stays short (the full date is in the Time card): the time, the weekday
  // within a week, the date beyond that.
  const days = Math.abs(dayNumber(now) - dayNumber(new Date()));
  const short = days === 0 ? fmtTime(now) : days < 7 ? `${fmtDate(now).split(/,? /)[0]} ${fmtTime(now)}` : fmtDate(now, dayKey(now).slice(0, 4) !== dayKey(new Date()).slice(0, 4)).replace(/^\w+,? /, '');
  const running = rate !== 1 && rate !== 0;
  return (
    <header class="topbar" ref={bar}>
      <button class="chip" onClick={() => open('location')} aria-label={`${name}, change location`}>
        <Icon name="pin" size={15} />
        <span class="chip-text">{name}</span>
      </button>
      {live ? (
        <button class="chip chip-time" onClick={() => open('time')} aria-label={`${fmtTime(now)}, live. Change time`}>
          <span class="live-dot" />
          <span class="chip-text tnum">{fmtTime(now)}</span>
        </button>
      ) : (
        <div class="chip chip-split">
          <button onClick={() => open('time')} aria-label={`${short}: ${fmtDateTime(now)}${rate === 0 ? ', paused' : running ? `, running ${rate < 0 ? 'backwards at ' : ''}${Math.round(Math.abs(rate))} times real speed` : ''}. Change time`}>
            <Icon name={rate === 0 ? 'pause' : running ? (rate > 0 ? 'forward' : 'back') : 'clock'} size={15} />
            <span class="chip-text tnum">{short}</span>
          </button>
          <span class="chip-divider" aria-hidden="true" />
          <button class="chip-now" onClick={goLive} aria-label="Now: back to the live sky">Now</button>
        </div>
      )}
      {/* Help (src/ui/HelpPanel.tsx): the tour of the buttons and how to do everyday things. */}
      <button class="chip chip-round chip-help" onClick={() => open('help')} aria-label={N.help}>
        <Icon name="help" size={19} stroke={1.9} />
      </button>
      <button class="chip chip-round chip-right" onClick={() => open('settings')} aria-label="Settings">
        <Icon name="gear" size={19} />
      </button>
    </header>
  );
}

/** Days since 1970 in the place's calendar, for "how many days away" without time-zone slips. */
const dayNumber = (d: Date) => Math.round(Date.parse(dayKey(d)) / 86400e3);

// ---------- Dock ----------
export function Dock() {
  const mode = viewMode.value;
  const p = panel.value;
  // With any panel open the dock rests: the small time card covers it, and a modal sheet puts the whole page out of
  // reach anyway.
  const nav = useRef<HTMLElement>(null);
  useOutOfReach(nav, p !== null);
  const toggleAR = async () => {
    if (!services.peek()) return;
    // Phones only allow sound that starts from a tap: prepare the voice now if names are to be said.
    if (settings.peek().sayPointing) unlockNarration();
    if (viewMode.peek() === 'ar') return stopPointing();
    if (pointingPaused.peek()) return resumePointing();
    explainStart(await startPointing());
  };
  const item = (name: string, label: string, target: Panel) => (
    <button class={`dock-btn ${p === target ? 'on' : ''}`} onClick={() => open(target)} aria-current={p === target ? 'page' : undefined}>
      <Icon name={name} size={24} />
      <span>{label}</span>
    </button>
  );
  return (
    <nav class="dock" aria-label="Main" ref={nav}>
      {item('search', 'Search', 'search')}
      {item('tonight', 'Tonight', 'tonight')}
      <button class={`dock-main ${mode === 'ar' ? 'on' : ''}`} onClick={toggleAR} aria-label={mode === 'ar' ? 'Explore by touch (stop pointing)' : 'Point at the sky'}>
        <span class="disc"><Icon name={mode === 'ar' ? 'hand' : 'phone'} size={20} /></span>
        <span>{mode === 'ar' ? 'Explore' : 'Point'}</span>
      </button>
      {item('layers', 'Sky view', 'layers')}
      {item('grid', 'More', 'more')}
    </nav>
  );
}

/** First visit: one quiet hint until something in the sky has been tapped. */
export function CoachMark() {
  const [done, setDone] = useState(() => !!loadJSON<{ v?: number }>('coached', {}).v);
  useEffect(() => {
    if (done) return;
    const finish = () => {
      saveJSON('coached', { v: 1 });
      setDone(true);
    };
    const off = selection.subscribe((s) => s && finish());
    const id = setTimeout(finish, 20000);
    return () => {
      off();
      clearTimeout(id);
    };
  }, [done]);
  if (done || panel.value || selection.value || viewMode.value !== 'manual' || pointingPaused.value || pointingPrompt.value) return null;
  return (
    <div class="coach" role="note">
      <Icon name="hand" size={17} /> Tap anything in the sky to see what it is
    </div>
  );
}

// ---------- Guidance to a selected object (AR) ----------
export function GuideChip() {
  const [, tick] = useState(0);
  const said = useRef({ text: '', at: 0 });
  useEffect(() => {
    const id = setInterval(() => tick((x) => x + 1), 200);
    return () => clearInterval(id);
  }, []);
  // The chosen object, or (reliving a night) the thing just picked on its timeline.
  const sel = selection.value ?? reliveFocus.value;
  const s = services.value;
  if (viewMode.value !== 'ar' || !sel || !s || aligning.value) return null;
  const loc = s.engine.locate(sel);
  if (!loc) return null;
  const t = enuToAltAz(refractEnu(loc.enu));
  const c = s.engine.viewCentre();
  // The turn is the plain difference in compass direction: how far to turn your body.
  const dAz = ((t.az - c.az + 540) % 360) - 180;
  const dAlt = t.alt - c.alt;
  const f = altAzToEnu(c.alt, c.az);
  const off = (Math.acos(Math.max(-1, Math.min(1, f[0] * loc.enu[0] + f[1] * loc.enu[1] + f[2] * loc.enu[2]))) * 180) / Math.PI;
  const close = off < 4;
  const parts: string[] = [];
  if (t.alt > 78) parts.push('look almost straight up');
  else if (Math.abs(dAz) >= 3) parts.push(`turn ${dAz > 0 ? 'right' : 'left'} ${Math.round(Math.abs(dAz))}°`);
  if (Math.abs(dAlt) >= 3 && t.alt <= 78) parts.push(`${dAlt > 0 ? 'raise' : 'lower'} ${Math.round(Math.abs(dAlt))}°`);
  const label = hideLabels.value ? 'The marked object' : loc.label;
  const text = close ? `${label} is right there` : `${label}: ${parts.join(', ')}`;
  // Screen readers hear the coarse instruction at most every few seconds, not every update.
  const coarse = close ? text : `${label}: ${parts.map((p) => p.replace(/ \d+°/, '')).join(', ')}`;
  const now = Date.now();
  if (coarse !== said.current.text && now - said.current.at > 2500) said.current = { text: coarse, at: now };
  return (
    <div class={`guide ${close ? 'on-target' : ''}`}>
      <Icon name={close ? 'check' : 'target'} size={16} />
      <span aria-hidden="true">{text}</span>
      <span class="sr-only" aria-live="polite">{said.current.text}</span>
    </div>
  );
}

// ---------- Pointing paused (a finger took over, or the phone lies flat) ----------
export function PausedChip() {
  const why = pointingPaused.value;
  if (!why || viewMode.value !== 'manual' || panel.value || selection.value) return null;
  return (
    <button class="pointing paused-chip" onClick={() => resumePointing()}>
      <Icon name="phone" size={16} />
      <span>{why === 'flat' ? 'Lift your phone to point' : 'Back to pointing'}</span>
    </button>
  );
}

// ---------- On an iPhone, motion access needs a tap ----------
export function PointPrompt() {
  if (!pointingPrompt.value || viewMode.value !== 'manual' || panel.value || selection.value) return null;
  const start = async () => {
    if (settings.peek().sayPointing) unlockNarration();
    explainStart(await startPointing());
  };
  return (
    <div class="point-prompt" role="dialog" aria-label="Point at the sky">
      <button class="btn big" onClick={start}><Icon name="phone" size={19} /> Point at the sky</button>
      <button class="btn-ghost" onClick={() => (pointingPrompt.value = false)}>Explore by touch</button>
    </div>
  );
}

// ---------- Pointing chip (AR) ----------
export function PointingChip() {
  const pt = pointingAt.value;
  const s = services.value;
  const key = pt && s ? nameClipKey(s, pt.sel) : null;
  // Screen readers hear what is in the circle at most every few seconds, not on every movement.
  const said = useRef({ text: '', at: 0 });
  const now = Date.now();
  if (pt && pt.label !== said.current.text && now - said.current.at > 2500) said.current = { text: pt.label, at: now };
  // Optionally say the name once the phone has settled on something for a moment.
  useEffect(() => {
    if (!key || !settings.peek().sayPointing || viewMode.peek() !== 'ar') return;
    // Not over a card: its Listen would be cut off by the name of whatever the phone drifts onto. Nor over anything else
    // being read aloud (the Arabic of a hadith on an open page, say), and only while names are still wanted.
    const id = setTimeout(() => {
      const reading = speaking.peek()?.[0];
      if (selection.peek() || (reading && !reading.startsWith('name-')) || !settings.peek().sayPointing || viewMode.peek() !== 'ar') return;
      void speak([key], false);
    }, 1200);
    return () => clearTimeout(id);
  }, [key]);
  // While lining up, the name in the circle would be read off a sky not yet lined up: it steps aside.
  if (viewMode.value !== 'ar' || !pt || selection.value || hideLabels.value || aligning.value) return null;
  return (
    <div class="pointing-wrap">
      <button class="pointing" onClick={() => (selection.value = pt.sel)}>
        <Icon name="target" size={16} />
        <span>{pt.label}</span>
        <Icon name="chevron" size={14} />
      </button>
      <span class="sr-only" aria-live="polite">{said.current.text}</span>
      {key && (
        <button class="pointing say" aria-label={`Say “${pt.label}”`} onClick={() => { unlockNarration(); void speak([key], false); }}>
          <Icon name="speaker" size={16} />
        </button>
      )}
    </div>
  );
}

/** In pointing mode: one tap to correct an offset compass, flagged whenever the compass says it is unsure. */
export function AlignChip() {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const s = services.value;
  if (viewMode.value !== 'ar' || !s || panel.value || selection.value || aligning.value) return null;
  // An iPhone learning north (at the start, or back from the background) holds the view still: say why, and how to
  // help (the compass is surest with the camera near level).
  if (!s.engine.orientation.northKnown) {
    return (
      <div class="align-chip warn finding" role="status">
        <Icon name="target" size={14} /> Finding north. Point the phone at the horizon for a moment.
      </div>
    );
  }
  const acc = s.engine.orientation.headingAccuracy; // iPhone only; −1 when unknown
  const unsure = acc > 20;
  return (
    <button class={`align-chip ${unsure ? 'warn' : ''}`} onClick={beginAligning} aria-label="Align with the sky">
      <Icon name="target" size={14} /> {unsure ? `Compass unsure (±${Math.round(acc)}°) · Align` : 'Align'}
    </button>
  );
}

/** Banners stack under the top bar: another place, another light, then one kind of thing. */
const bannerTop = (row: number) => ({ top: `calc(var(--safe-top) + ${54 + 44 * row}px)` });

/** Shown while the sky is drawn in another kind of light. */
export function BandBanner() {
  const b = skyBand.value;
  if (!b) return null;
  return (
    <button class="preview-banner band-banner" style={bannerTop(previewFrom.value ? 1 : 0)} onClick={() => (skyBand.value = null)}>
      <Icon name="sparkle" size={14} /> <span class="chip-text">Sky in {b === 'halpha' ? 'hydrogen-alpha' : b} light</span> <span class="preview-back">Back to visible</span>
    </button>
  );
}

/** The survey's credit, on screen while its photographs are showing (its ODbL licence asks for a notice). */
export function SurveyCredit() {
  if (!surveyShown.value) return null;
  return (
    <a class="survey-credit" href="https://archive.stsci.edu/dss/copyright.html" target="_blank" rel="noopener">
      Photos: DSS2 © Caltech, AAO, PPARC, AURA · STScI/NASA · CDS
    </a>
  );
}

/** Shown while previewing the sky from somewhere else (e.g. a suggested dark site). */
export function PreviewBanner() {
  const f = previewFrom.value;
  // Reliving a night has its own banner.
  if (!f || f.kind === 'relive') return null;
  return (
    <button class="preview-banner" style={bannerTop(0)} onClick={endPreview}>
      <Icon name="pin" size={14} /> <span class="chip-text">Sky at {f.label}</span> <span class="preview-back">Back to {f.home.name}</span>
    </button>
  );
}

/** The live region stays in the page so every message is read out, not just some. */
export function Toast() {
  const t = toast.value;
  return (
    <div role="status" aria-live="polite">
      {t && (
        <div class={`toast ${t.action ? 'with-action' : ''}`} key={t.id}>
          <span>{t.text}</span>
          {t.action && (
            <button class="toast-action" onClick={() => { t.action!.run(); toast.value = null; }}>{t.action.label}</button>
          )}
        </div>
      )}
    </div>
  );
}

/** Night vision's colour temperature (kelvin) for a warmth from 0 (less warm) to 1 (more warm). */
export const nightKelvin = (warmth: number) => 5000 * Math.pow(1500 / 5000, Math.min(1, Math.max(0, warmth)));

/**
 * What the whole screen is multiplied by, like Night Shift: the colour of light at that temperature, as
 * red, green and blue from 0 to 1 (after Tanner Helland's fit to black-body colours). Warmer passes less
 * blue and then less green; the brightness stays as it is.
 */
export function nightTint(warmth: number): [number, number, number] {
  const t = nightKelvin(warmth) / 100;
  const g = t <= 66 ? 99.4708025861 * Math.log(t) - 161.1195681661 : 288.1221695283 * Math.pow(t - 60, -0.0755148492);
  const b = t >= 66 ? 255 : t <= 19 ? 0 : 138.5177312231 * Math.log(t - 10) - 305.0447927307;
  const c = (v: number) => Math.min(1, Math.max(0, v / 255));
  return [1, c(g), c(b)];
}

export function NightFilter() {
  const s = settings.value;
  if (!s.nightMode) return null;
  const [r, g, b] = nightTint(s.nightWarmth ?? 0.5);
  return <div class="night-filter" style={{ background: `rgb(${Math.round(r * 255)},${Math.round(g * 255)},${Math.round(b * 255)})` }} aria-hidden="true" />;
}

// ---------- Small building blocks ----------
export function Stat({ label, value, sub }: { label: string; value: ComponentChildren; sub?: ComponentChildren }) {
  return (
    <div class="stat">
      <div class="stat-label">{label}</div>
      <div class="stat-value">{value}</div>
      {sub && <div class="stat-sub">{sub}</div>}
    </div>
  );
}

export function Toggle({ on, onChange, label, sub, icon }: { on: boolean; onChange: (v: boolean) => void; label: string; sub?: string; icon?: string }) {
  return (
    <label class="toggle-row">
      {icon && <span class="row-icon"><Icon name={icon} size={18} /></span>}
      <span class="row-text">
        <span class="row-label">{label}</span>
        {sub && <span class="row-sub">{sub}</span>}
      </span>
      <input type="checkbox" role="switch" checked={on} onChange={(e) => onChange((e.target as HTMLInputElement).checked)} />
      <span class="switch" aria-hidden="true" />
    </label>
  );
}

export function Segmented<T extends string | number>({ value, options, onChange, label }: { value: T; options: [T, string][]; onChange: (v: T) => void; label?: string }) {
  return (
    <div class="seg" role="radiogroup" aria-label={label}>
      {options.map(([v, l]) => (
        <button key={String(v)} role="radio" aria-checked={v === value} class={v === value ? 'on' : ''} onClick={() => onChange(v)}>
          {l}
        </button>
      ))}
    </div>
  );
}
